import { Files } from '@core/interfaces/claims/files.interface';

const FILE_READ_PROBE_BYTES = 16;

/**
 * Environment and per-file diagnostics captured right before a multipart
 * submission. Mobile WebKit can invalidate the backing store of picked File
 * objects while a wizard is open (camera use, backgrounding, memory
 * pressure); sending such a file makes Safari silently submit an empty
 * request body, which the API rejects with an empty-model 400. These fields
 * let App Insights tell that scenario apart from network loss or client bugs.
 */
export interface IUploadEvidence {
  fileCount: number;
  totalFileBytes: number;
  /** JSON array of {name, size, type, lastModified, docType, readable} per file */
  filesInfo: string;
  unreadableCount: number;
  unreadableFiles: string;
  online: boolean;
  visibilityState: string;
  serviceWorkerControlled: boolean;
  connectionType: string;
  deviceMemory: string;
}

export interface IUploadEvidenceReport {
  evidence: IUploadEvidence;
  unreadableFileNames: string[];
}

/**
 * Probes whether the browser can still read the file's bytes. A file that was
 * readable when picked can become unreadable later (see IUploadEvidence);
 * posting it anyway produces an empty request body on WebKit.
 */
const isFileReadable = async (file: File | null): Promise<boolean> => {
  if (!file || typeof file.slice !== 'function') {
    return false;
  }
  const probe = file.slice(0, FILE_READ_PROBE_BYTES);
  if (typeof probe.arrayBuffer !== 'function') {
    return true;
  }
  try {
    await probe.arrayBuffer();
    return true;
  } catch (e) {
    return false;
  }
};

export const collectUploadEvidence = async (files: Files[]): Promise<IUploadEvidenceReport> => {
  const filesEvidence = await Promise.all(
    files.map(async file => ({
      name: file.originalFile?.name ?? file.fileName ?? 'unknown',
      size: file.originalFile?.size ?? file.size ?? -1,
      type: file.originalFile?.type ?? file.mimeType ?? '',
      lastModified: file.originalFile?.lastModified ?? -1,
      docType: file.docType ?? null,
      readable: await isFileReadable(file.originalFile),
    })),
  );
  const unreadableFileNames = filesEvidence.filter(file => !file.readable).map(file => file.name);
  const nav = navigator as Navigator & {
    connection?: {effectiveType?: string};
    deviceMemory?: number;
  };
  const evidence: IUploadEvidence = {
    fileCount: files.length,
    totalFileBytes: filesEvidence.reduce((sum, file) => sum + Math.max(file.size, 0), 0),
    filesInfo: JSON.stringify(filesEvidence),
    unreadableCount: unreadableFileNames.length,
    unreadableFiles: unreadableFileNames.join(', '),
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    serviceWorkerControlled: !!navigator.serviceWorker?.controller,
    connectionType: nav.connection?.effectiveType ?? 'unknown',
    deviceMemory: `${nav.deviceMemory ?? 'unknown'}`,
  };

  return { unreadableFileNames, evidence };
};
